import { useCallback } from 'react';
import { useAppDispatch } from 'store/app-store-hooks';
import {
    EmailMessagePayloadWithAttachments,
    SetSharedClosedReportDataProps,
    SupportRequest,
} from 'core/entities';
import {
    clearOfflineReportsRequestComments as _clearOfflineReportsRequestComments,
    clearOfflineReportsSubmitStatus as _clearOfflineReportsSubmitStatus,
    clearReportDetails as _clearReportDetails,
    getOfflineReportDetails,
    getOfflineReports,
    getOfflineReportsAccount as _getOfflineReportsAccount,
    getOfflineReportsConfigurations,
    getOfflineReportsCreditsBalance as _getOfflineReportsCreditsBalance,
    getOfflineReportsRequests as _getOfflineReportsRequests,
    getOfflineReportsRequestsComments as _getOfflineReportsRequestsComments,
    getOfflineReportsSharedWithMe as _getOfflineReportsSharedWithMe,
    getOfflineReportsUserAccountInfo as _getOfflineReportsUserAccountInfo,
    getSupportRequests as _getSupportRequests,
    openInsufficientCreditsPopup as _openInsufficientCreditsPopup,
    postCaseEmailMsg as _postCaseEmailMsg,
    postOfflineReportsRequestsComment as _postOfflineReportsRequestsComment,
    setIsSubmitLoadingPopupVisible as _setIsSubmitLoadingPopupVisible,
    setSupportRequestsLoading as _setSupportRequestsLoading,
    setSupportSearchText as _setSupportSearchText,
    setSupportSelectedRequest as _setSupportSelectedRequest,
    setSupportStatuses as _setSupportStatuses,
    setSupportTypes as _setSupportTypes,
    updateClosedReportSharedAndTargetValues,
    updateOfflineReportsOrderRequestReadReceipt,
} from 'store/offline-reports/actions/get-offline-reports';

export const useOfflineReportsActions = () => {
    const dispatch = useAppDispatch();

    const getReports = useCallback(() => {
        dispatch(getOfflineReports());
    }, [dispatch]);

    const getReportDetails = useCallback(
        (id: string) => {
            dispatch(getOfflineReportDetails(id));
        },
        [dispatch],
    );

    const clearReportDetails = useCallback(() => {
        dispatch(_clearReportDetails());
    }, [dispatch]);

    const getOfflineReportsConfiguration = useCallback(() => {
        dispatch(getOfflineReportsConfigurations());
    }, [dispatch]);

    const getOfflineReportsRequests = useCallback(() => {
        dispatch(_getOfflineReportsRequests());
    }, [dispatch]);

    const getOfflineReportsSharedWithMe = useCallback(() => {
        dispatch(_getOfflineReportsSharedWithMe());
    }, [dispatch]);

    const getOfflineReportsAccount = useCallback(() => {
        dispatch(_getOfflineReportsAccount());
    }, [dispatch]);

    const getOfflineReportsUserAccountInfo = useCallback(() => {
        dispatch(_getOfflineReportsUserAccountInfo());
    }, [dispatch]);

    const getOfflineReportsRequestsComments = useCallback(
        (requestId: string) => {
            dispatch(
                _getOfflineReportsRequestsComments({
                    requestId,
                }),
            );
        },
        [dispatch],
    );

    const postOfflineReportsRequestsComment = useCallback(
        async ({ requestId, payload }) => {
            await dispatch(
                _postOfflineReportsRequestsComment({
                    requestId,
                    payload,
                }),
            );
        },
        [dispatch],
    );

    const postCaseEmailMsg = useCallback(
        async (payload: EmailMessagePayloadWithAttachments) => {
            return dispatch(
                _postCaseEmailMsg({
                    payload,
                }),
            );
        },
        [dispatch],
    );

    const clearOfflineReportsRequestComments = useCallback(() => {
        dispatch(_clearOfflineReportsRequestComments());
    }, [dispatch]);

    const clearOfflineReportsSubmitStatus = useCallback(() => {
        dispatch(_clearOfflineReportsSubmitStatus());
    }, [dispatch]);

    const openInsufficientCreditsPopup = useCallback(() => {
        dispatch(_openInsufficientCreditsPopup());
    }, [dispatch]);

    const setIsSubmitLoadingPopupVisible = useCallback(
        (flag: boolean) => {
            dispatch(_setIsSubmitLoadingPopupVisible(flag));
        },
        [dispatch],
    );

    const getOfflineReportsCreditsBalance = useCallback(() => {
        dispatch(_getOfflineReportsCreditsBalance());
    }, [dispatch]);

    const setOfflineReportsRequestAsRead = useCallback(
        (requestId: string) => {
            dispatch(
                updateOfflineReportsOrderRequestReadReceipt({
                    requestId,
                    flag: true,
                }),
            );
        },
        [dispatch],
    );

    const setClosedReportSharedDataValue = useCallback(
        (params: SetSharedClosedReportDataProps) => {
            dispatch(updateClosedReportSharedAndTargetValues(params));
        },
        [dispatch],
    );

    const getSupportRequests = useCallback(() => {
        dispatch(_getSupportRequests());
    }, [dispatch]);

    const setSupportStatuses = useCallback(
        (statuses: string[]) => {
            dispatch(_setSupportStatuses(statuses));
        },
        [dispatch],
    );

    const setSupportTypes = useCallback(
        (types: string[]) => {
            dispatch(_setSupportTypes(types));
        },
        [dispatch],
    );

    const setSupportSearchText = useCallback(
        (searchText: string) => {
            dispatch(_setSupportSearchText(searchText));
        },
        [dispatch],
    );

    const setSupportSelectedRequest = useCallback(
        (request: SupportRequest | undefined) => {
            dispatch(_setSupportSelectedRequest(request));
        },
        [dispatch],
    );

    const setSupportRequestsLoading = useCallback(
        (isLoading: boolean) => {
            dispatch(_setSupportRequestsLoading(isLoading));
        },
        [dispatch],
    );

    return {
        getReports,
        getReportDetails,
        clearReportDetails,
        getOfflineReportsConfiguration,
        getOfflineReportsRequests,
        getOfflineReportsSharedWithMe,
        getOfflineReportsAccount,
        getOfflineReportsUserAccountInfo,
        openInsufficientCreditsPopup,
        setIsSubmitLoadingPopupVisible,
        getOfflineReportsRequestsComments,
        postOfflineReportsRequestsComment,
        postCaseEmailMsg,
        clearOfflineReportsRequestComments,
        clearOfflineReportsSubmitStatus,
        setOfflineReportsRequestAsRead,
        getOfflineReportsCreditsBalance,
        setClosedReportSharedDataValue,
        getSupportRequests,
        setSupportStatuses,
        setSupportTypes,
        setSupportSearchText,
        setSupportSelectedRequest,
        setSupportRequestsLoading,
    };
};
